import { useMemo, useState } from 'react'

import { useGetCompany } from '@/api/company.hook'
import { useIsControlCommentRequired } from '@/api/control-status-comment-setting.hook'
import { useBulkUpdateControlStatus } from '@/api/control.hook'
import { ControlStatus } from '@/gen/controls/v1/control_status_pb'
import { ControlStatusChangeReason } from '@/gen/inventory/v1/control_service_pb'

import { controlStatusLabel } from '@/const/label'

import { useTrackCallback } from '@/lib/analytics/events'
import { useSelectableControlStatuses } from '@/lib/app/attested-status-access.hook'

import { BULK_UPDATE_STATUS_MODAL_LABELS } from '@/pages/assessment/assessment-tabs/bulk/labels'

import { BulkEditFieldModal } from '@/components/lemmala-design-system/bulk-edit-field-modal/bulk-edit-field-modal'
import { useToast } from '@/components/lemmala-design-system/toast/use-toast'
import { ControlStatusIcon } from '@/components/shared/icons/control-status'

type BulkUpdateStatusModalProps = {
  assessmentId: string
  selectedControlIds: string[]
  tab: 'issues' | 'controls'
  companyId: string
  onClose: () => void
}

export const BulkUpdateStatusModal = ({
  assessmentId,
  selectedControlIds,
  tab,
  companyId,
  onClose,
}: BulkUpdateStatusModalProps) => {
  const { toast } = useToast()
  const [comment, setComment] = useState('')
  const isCommentRequired = useIsControlCommentRequired()
  const selectableStatuses = useSelectableControlStatuses()

  const { mutateAsync, isPending } = useBulkUpdateControlStatus()
  const { data: companyData } = useGetCompany(companyId)
  const trackingProps = {
    company: companyData?.company?.profile?.name,
    company_id: companyId,
    assessment_id: assessmentId,
  }

  const trackStatusSelected = useTrackCallback('assessment.controls.status-selected')
  const trackConfirmed = useTrackCallback('assessment.controls.confirmed')
  const trackCompleted = useTrackCallback('assessment.controls.completed')
  const trackFailed = useTrackCallback('assessment.controls.failed')

  const options = useMemo(
    () =>
      selectableStatuses.map((status) => ({
        value: String(status),
        label: (
          <span className='flex items-center gap-2'>
            <ControlStatusIcon status={status} />
            {controlStatusLabel[status]}
          </span>
        ),
        searchText: controlStatusLabel[status],
      })),
    [selectableStatuses],
  )

  const handleValueChange = (value: string) => {
    const status = Number(value) as ControlStatus
    trackStatusSelected({
      ...trackingProps,
      new_status: controlStatusLabel[status] ?? BULK_UPDATE_STATUS_MODAL_LABELS.KEEP_AS_IS_OPTION,
    })
  }

  const handleConfirm = async (value: string) => {
    const status = Number(value) as ControlStatus
    trackConfirmed({
      ...trackingProps,
      tab,
      selected_count: selectedControlIds.length,
      new_status: controlStatusLabel[status],
      reason: ControlStatusChangeReason.UNSPECIFIED,
      has_comment: !!comment,
    })

    const { successes, failures } = await mutateAsync({
      controlIds: selectedControlIds,
      status,
      reason: ControlStatusChangeReason.UNSPECIFIED,
      comment: comment || undefined,
    })

    if (failures.length > 0) {
      trackFailed({
        ...trackingProps,
        selected_count: selectedControlIds.length,
        error_type: String(failures[0].error),
      })
      console.error('Failed to update control statuses:', failures)
      toast({
        variant: 'destructive',
        title: BULK_UPDATE_STATUS_MODAL_LABELS.ERROR_TITLE,
        description: BULK_UPDATE_STATUS_MODAL_LABELS.ERROR_DESCRIPTION,
      })
      return
    }

    trackCompleted({
      ...trackingProps,
      selected_count: selectedControlIds.length,
      updated_count: successes.length,
      failed_count: failures.length,
      success_type: 'full',
    })
    toast({
      variant: 'success',
      title: BULK_UPDATE_STATUS_MODAL_LABELS.SUCCESS_TITLE,
      description: BULK_UPDATE_STATUS_MODAL_LABELS.SUCCESS_DESCRIPTION(selectedControlIds.length),
    })
    onClose()
  }

  return (
    <BulkEditFieldModal
      title={BULK_UPDATE_STATUS_MODAL_LABELS.TITLE}
      subtitle={BULK_UPDATE_STATUS_MODAL_LABELS.SUBTITLE(selectedControlIds.length)}
      fieldLabel={BULK_UPDATE_STATUS_MODAL_LABELS.VALIDATION_STATUS_LABEL}
      options={options}
      isPending={isPending}
      onConfirm={handleConfirm}
      onClose={onClose}
      onValueChange={handleValueChange}
      comment={comment}
      onCommentChange={setComment}
      isCommentRequired={isCommentRequired}
    />
  )
}
